/**
 * Cognito SDK glue.
 *
 * Wraps `amazon-cognito-identity-js` so the rest of the app sees a
 * promise-based, narrowly-typed surface.
 *
 * Two stores hold the session, and they are NOT peers:
 *
 *   - The `.3mote.com` cookies (`./cookie-helpers.ts`) are the source of
 *     truth. They cross subdomains, so a sign-in on the central auth
 *     origin (`login[-staging].3mote.com`) is valid on every tenant host.
 *   - The SDK's own localStorage cache (`CognitoIdentityServiceProvider.
 *     <clientId>.*`) is a per-origin DERIVED cache. localStorage never
 *     crosses origins, so after the central-login hop a tenant host holds
 *     the cookies and an empty SDK cache. `resolveCurrentUser()` is the
 *     one seam that answers "who is the SDK user": it reuses the cache
 *     when it matches the cookie identity and otherwise re-seeds it from
 *     the cookies. Nothing else may call `pool.getCurrentUser()`.
 *
 * Hydrating from cookies passes `ClockDrift: 0` explicitly. Left unset,
 * the SDK derives drift as `now - iat`, which for stale tokens makes
 * `isValid()` report true forever (the drift-adjusted clock collapses
 * onto the issue time) — `getSession()` would then hand back expired
 * tokens and `globalSignOut` would send one. Fresh tokens (password
 * sign-in, SSO adoption) let the SDK compute drift normally.
 *
 * Pool + client IDs come from Vite env vars baked in at build time:
 *   - VITE_COGNITO_USER_POOL_ID
 *   - VITE_COGNITO_CLIENT_ID
 *   - VITE_COGNITO_REGION  (informational; the JS SDK derives the region
 *     from the pool ID, but we expose it for symmetry with the BE config)
 */

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import type { ICognitoUserPoolData, ICognitoUserSessionData } from 'amazon-cognito-identity-js'

import {
  clearTokens,
  getAccessToken,
  getIdToken,
  getRefreshToken,
  setTokens,
} from './cookie-helpers'

interface SignUpResult {
  userSub: string
  codeDeliveryDetails: unknown
}

interface SignInResult {
  accessToken: string
  idToken: string
  refreshToken: string
}

interface SessionTokens {
  accessToken: string
  idToken: string
}

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID

let cachedPool: CognitoUserPool | null = null

function getUserPool(): CognitoUserPool {
  if (cachedPool) return cachedPool
  if (!userPoolId || !clientId) {
    throw new Error(
      'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.',
    )
  }
  const data: ICognitoUserPoolData = {
    UserPoolId: userPoolId,
    ClientId: clientId,
  }
  cachedPool = new CognitoUserPool(data)
  return cachedPool
}

function buildCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: getUserPool() })
}

function extractTokens(session: CognitoUserSession): SignInResult {
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  }
}

export function signUp(email: string, password: string, name: string): Promise<SignUpResult> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ]
    getUserPool().signUp(email, password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error('Cognito signUp returned no result'))
        return
      }
      resolve({
        userSub: result.userSub,
        codeDeliveryDetails: result.codeDeliveryDetails,
      })
    })
  })
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    buildCognitoUser(email).confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

/**
 * Cognito's resend / forgot-password APIs return a `CodeDeliveryDetails`
 * blob shaped roughly:
 *   { Destination: 'd***@example.com', DeliveryMedium: 'EMAIL', AttributeName: 'email' }
 * The SDK types it loosely as `any` — we narrow to the destination string
 * and surface it so the FE can render "We sent a code to {masked}".
 */
interface CodeDeliveryDetailsLike {
  CodeDeliveryDetails?: { Destination?: string }
  Destination?: string
}

function extractDestination(payload: unknown, fallback: string): string {
  const p = payload as CodeDeliveryDetailsLike | undefined
  return p?.CodeDeliveryDetails?.Destination ?? p?.Destination ?? fallback
}

export function resendConfirmationCode(email: string): Promise<{ destination: string }> {
  return new Promise((resolve, reject) => {
    buildCognitoUser(email).resendConfirmationCode((err, result) => {
      if (err) {
        reject(err)
        return
      }
      resolve({ destination: extractDestination(result, email) })
    })
  })
}

/**
 * Initiates the Cognito self-serve password reset flow. Cognito mails the
 * user a 6-digit verification code; the user then submits that code +
 * a new password to `confirmPassword` (next function).
 *
 * Cognito's `preventUserExistenceErrors: true` (set in the User Pool, see
 * `3mote-infra-new/lib/auth-stack.ts`) means an unknown email returns the
 * same shape as a known one — the UI should always show the same "If an
 * account exists, we've emailed a code" success state.
 */
export function forgotPassword(email: string): Promise<{ destination: string }> {
  return new Promise((resolve, reject) => {
    buildCognitoUser(email).forgotPassword({
      onSuccess: (data) => resolve({ destination: extractDestination(data, email) }),
      // Cognito surfaces the masked destination via the inputVerificationCode
      // callback in newer pools (and onSuccess in older ones); both branches
      // resolve with the same shape so the caller never has to care.
      inputVerificationCode: (data) => resolve({ destination: extractDestination(data, email) }),
      onFailure: (err) => reject(err),
    })
  })
}

export function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    buildCognitoUser(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    })
    const cognitoUser = buildCognitoUser(email)
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const tokens = extractTokens(session)
        setTokens(tokens)
        resolve(tokens)
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        // Phase 1.5: surface a "set new password" UI. For now, fail loudly.
        reject(new Error('NEW_PASSWORD_REQUIRED'))
      },
    })
  })
}

interface ExternalSessionBundle {
  idToken: string
  accessToken: string
  refreshToken: string
}

/**
 * Canonical pool Username from an ID token: `cognito:username`, falling
 * back to `sub` (the pool uses sub-shaped usernames for email-alias
 * sign-ins). `decodePayload()` yields `{}` on garbage, so this never
 * throws — it returns `null` when neither claim is present.
 */
function usernameFromIdToken(idToken: CognitoIdToken): string | null {
  const payload = idToken.decodePayload() as { 'cognito:username'?: string; sub?: string }
  return payload['cognito:username'] ?? payload.sub ?? null
}

interface SeedOptions {
  /**
   * Explicit clock drift for the seeded session. Pass `0` when the tokens
   * may already be stale (cookie hydration); omit for fresh tokens so the
   * SDK computes drift the same way it does after `authenticateUser`.
   */
  clockDrift?: number
}

/**
 * Populate this origin's SDK cache from a token bundle and return the user.
 * `setSignInUserSession` runs `cacheTokens()`, which writes `LastAuthUser`
 * + the per-user token keys — the same write path the SDK takes after a
 * password sign-in, so the seed is persistent, not in-memory only.
 */
function seedSdkUser(bundle: ExternalSessionBundle, options: SeedOptions = {}): CognitoUser {
  const idToken = new CognitoIdToken({ IdToken: bundle.idToken })
  const username = usernameFromIdToken(idToken)
  if (!username) {
    throw new Error('ID token carries neither a cognito:username nor a sub claim.')
  }
  const user = new CognitoUser({ Username: username, Pool: getUserPool() })
  // The SDK's `.d.ts` omits `ClockDrift` from `ICognitoUserSessionData`
  // although the runtime constructor reads it — widen the type locally.
  const sessionData: ICognitoUserSessionData & { ClockDrift?: number } = {
    IdToken: idToken,
    AccessToken: new CognitoAccessToken({ AccessToken: bundle.accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: bundle.refreshToken }),
  }
  if (options.clockDrift !== undefined) sessionData.ClockDrift = options.clockDrift
  user.setSignInUserSession(new CognitoUserSession(sessionData))
  return user
}

/**
 * Adopt a Cognito token bundle minted outside this SDK (the BE's SSO
 * bridge returns one after verifying a Google / Apple ID token). Writes
 * the cookies (source of truth) and seeds THIS origin's SDK cache so
 * refresh / sign-out here skip the hydration step. The tokens are fresh,
 * so the SDK computes clock drift itself. Any other origin hydrates
 * lazily from the cookies via `resolveCurrentUser()` — the cache half
 * is a convenience, not a requirement.
 */
export function adoptExternalSession(bundle: ExternalSessionBundle): void {
  seedSdkUser(bundle)
  setTokens(bundle)
}

/**
 * The SDK user for the current cookie session, hydrating this origin's
 * SDK cache from the cookies when it is empty or belongs to a different
 * identity. Returns `null` when there is neither a matching cache nor a
 * complete cookie session to hydrate from (nobody is signed in).
 */
function resolveCurrentUser(): CognitoUser | null {
  const idToken = getIdToken()
  const accessToken = getAccessToken()
  const refreshToken = getRefreshToken()
  const cookieUsername = idToken
    ? usernameFromIdToken(new CognitoIdToken({ IdToken: idToken }))
    : null

  const cached = getUserPool().getCurrentUser()
  if (cached) {
    if (cookieUsername === null || cached.getUsername() === cookieUsername) return cached
    // Leftover from a different account that signed in on this origin.
    // Clear it locally (no network) so it can't answer for the cookie
    // session, then re-seed below.
    cached.signOut()
  }

  if (!idToken || !accessToken || !refreshToken || !cookieUsername) return null
  try {
    // The cookie tokens may be stale — see the module header on `ClockDrift`.
    return seedSdkUser({ idToken, accessToken, refreshToken }, { clockDrift: 0 })
  } catch {
    return null
  }
}

export function signOut(): Promise<void> {
  const current = resolveCurrentUser()

  // Legacy parity: invalidate the access token BE-side via Cognito's
  // GlobalSignOut so a stolen JWT can't be replayed even if the local
  // cookie clear fails. Wrapped in a try/catch — sign-out must succeed
  // (locally) even if the network call errors.
  return new Promise<void>((resolve) => {
    const finalize = () => {
      try {
        current?.signOut()
      } catch {
        // ignore — local clear below still runs
      }
      clearTokens()
      resolve()
    }

    if (!current) {
      finalize()
      return
    }

    const accessToken = getAccessToken()
    if (!accessToken) {
      finalize()
      return
    }

    // `globalSignOut` refuses an expired session, so let `getSession` settle
    // it first: a valid (or freshly hydrated) session comes straight back,
    // an expired one is refreshed from the cached refresh token. Either way
    // the global sign-out carries a live access token. Both branches
    // finalize (clear local cookies + resolve).
    current.getSession((sessionErr: Error | null) => {
      if (sessionErr) {
        finalize()
        return
      }
      try {
        current.globalSignOut({
          onSuccess: () => finalize(),
          onFailure: () => finalize(),
        })
      } catch {
        finalize()
      }
    })
  })
}

/**
 * Decode a Cognito access token's payload (JWT middle segment) and return
 * the seconds-until-expiry (negative if already expired, null if the
 * token is malformed or has no `exp` claim).
 *
 * `amazon-cognito-identity-js` doesn't expose this directly, and we don't
 * verify the signature here — verification happens server-side. This is
 * just to pre-empt expired tokens from reaching the WebSocket handshake,
 * which would otherwise loop on close-1006 because the BE's $connect
 * Lambda's HTTP-401 doesn't translate to a 4401/4403 close code the FE
 * can detect (handshake-level rejection looks like a network error).
 */
export function jwtSecondsUntilExpiry(token: string): number | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const payloadSegment = parts[1]
  if (!payloadSegment) return null
  try {
    const padded = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    const payload = JSON.parse(json) as { exp?: number }
    if (typeof payload.exp !== 'number') return null
    return payload.exp - Math.floor(Date.now() / 1000)
  } catch {
    return null
  }
}

/**
 * Refresh the session from the cookie-stored refresh token. Used by the
 * API client on the first 401 so it can replay once before bouncing the
 * user to /login.
 *
 * Calls the SDK's `refreshSession` directly instead of touching
 * `getSession()` first: `getSession()` refreshes an expired cached session
 * on its own, so the pre-touch cost a second `InitiateAuth` per expiry,
 * and the SDK's `refreshSession` needs only a username + refresh token —
 * no live session.
 */
let inflightRefresh: Promise<SessionTokens> | null = null

export function refreshSession(): Promise<SessionTokens> {
  // Single-flight across EVERY caller, not just apiClient's own guard: the
  // WebSocket keepalive (two mounted consumers, plus StrictMode's doubled
  // effects in dev) calls this directly when the token is stale, and each
  // un-coalesced call is another `InitiateAuth`. One expiry → one refresh.
  if (inflightRefresh) return inflightRefresh
  inflightRefresh = performRefresh().finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

function performRefresh(): Promise<SessionTokens> {
  return new Promise((resolve, reject) => {
    const refreshToken = getRefreshToken()
    const current = refreshToken ? resolveCurrentUser() : null
    if (!current || !refreshToken) {
      reject(new Error('No active Cognito session to refresh.'))
      return
    }
    current.refreshSession(
      new CognitoRefreshToken({ RefreshToken: refreshToken }),
      (err?: Error, session?: CognitoUserSession) => {
        if (err || !session) {
          reject(err ?? new Error('Cognito refreshSession returned no session'))
          return
        }
        const tokens = extractTokens(session)
        setTokens(tokens)
        resolve({ accessToken: tokens.accessToken, idToken: tokens.idToken })
      },
    )
  })
}
